import React, { ReactElement } from 'react';

import t from '../../../labels/labelling-utils';
import { FontSize } from '../../../typings/design-tokens';
import { IComponent } from '../../../typings/generic-component';
import { hasPercentage, StatisticsId } from '../../../typings/statistics';
import { makeClassNames } from '../../../utils/styles';
import HorizontalBars from '../../atoms/horizontal-bars/horizontal-bars';
import { IIcon } from '../../atoms/icons/icon';
import IconAttemptsOnTarget from '../../atoms/icons/icon-attempts-on-target';
import IconAttemptsTotal from '../../atoms/icons/icon-attempts-total';
import IconBallPossession from '../../atoms/icons/icon-ball-possession';
import IconCorners from '../../atoms/icons/icon-corners';
import IconFoulsCommitted from '../../atoms/icons/icon-fouls-committed';
import IconOffside from '../../atoms/icons/icon-offside';
import IconRedCard from '../../atoms/icons/icon-red-card';
import IconWoodwork from '../../atoms/icons/icon-woodwork';
import IconYellowCard from '../../atoms/icons/icon-yellow-card';
import Text from '../../atoms/text/text';

import './match-statistics-row.scss';

const LABEL_FONT_SIZE: FontSize = '16--14';
const NUMBER_FONT_SIZE: FontSize = '16--26';

type Svg = {
  [key in StatisticsId]: ReactElement<IIcon>;
};

const props: IIcon = {
  size: 'small',
};

const classNames = () =>
  makeClassNames({
    matchStatisticsRow: 'match-statistics-row',
    iconCell: 'match-statistics-row__icon-cell',
    icon: 'match-statistics-row__icon',
    labelCell: 'match-statistics-row__label-cell',
    labelSpan: 'match-statistics-row__label-span',
    homeTeamPoint: 'match-statistics-row__home-cell',
    awayTeamPoint: 'match-statistics-row__away-cell',
    pointSpan: 'match-statistics-row__point-span',
    matchStatistics: 'match-statistics-row__statistics',
  });

const getSvgs = (cn: ReturnType<typeof classNames>): Svg => {
  return {
    possession_percentage: <IconBallPossession className={cn.icon} {...props} />,
    on_target_scoring_att: <IconAttemptsOnTarget className={cn.icon} {...props} />,
    total_scoring_att: <IconAttemptsTotal className={cn.icon} {...props} />,
    total_post: <IconWoodwork className={cn.icon} {...props} />,
    total_offside: <IconOffside className={cn.icon} {...props} />,
    fouls: <IconFoulsCommitted className={cn.icon} {...props} />,
    total_yel_card: <IconYellowCard className={cn.icon} {...props} />,
    total_red_card: <IconRedCard className={cn.icon} {...props} />,
    won_corners: <IconCorners className={cn.icon} {...props} />,
  };
};

const getLabelMap = () => {
  return {
    possession_percentage: t('match.statistics.label.possession_percentage'),
    on_target_scoring_att: t('match.statistics.label.on_target_scoring_att'),
    total_scoring_att: t('match.statistics.label.total_scoring_att'),
    fouls: t('match.statistics.label.fouls'),
    total_yel_card: t('match.statistics.label.total_yel_card'),
    total_red_card: t('match.statistics.label.total_red_card'),
    total_offside: t('match.statistics.label.total_offside'),
    total_post: t('match.statistics.label.total_post'),
    won_corners: t('match.statistics.label.won_corners'),
  };
};

export interface IMatchStatisticsRow extends IComponent {
  homeNumber: number;
  awayNumber: number;
  label: StatisticsId;
  isAnimated?: boolean;
}

const MatchStatisticsRow = ({ homeNumber, awayNumber, label, isAnimated = true }: IMatchStatisticsRow) => {
  const cn = classNames();
  const svgs = getSvgs(cn);
  const labelMap = getLabelMap();
  return (
    <tr className={cn.matchStatisticsRow}>
      <td className={cn.iconCell}>{svgs[label]}</td>
      <td className={cn.labelCell}>
        <Text className={cn.labelSpan} color="medium" fontSize={LABEL_FONT_SIZE}>
          {labelMap[label]}
        </Text>
      </td>
      <td className={cn.homeTeamPoint}>
        <Text className={cn.pointSpan} color="blue" fontSize={NUMBER_FONT_SIZE}>
          {homeNumber}
          {hasPercentage(label) ? '%' : ''}
        </Text>
      </td>
      <td className={cn.matchStatistics}>
        <HorizontalBars valueBar1={homeNumber} valueBar2={awayNumber} animated={isAnimated} />
      </td>
      <td className={cn.awayTeamPoint}>
        <Text className={cn.pointSpan} color="blue-light" fontSize={NUMBER_FONT_SIZE}>
          {awayNumber}
          {hasPercentage(label) ? '%' : ''}
        </Text>
      </td>
    </tr>
  );
};

export default MatchStatisticsRow;
